/**
 * **매장 셀렉트의 조회 규약 한 곳** — 의존성이 없는 순수 모듈이다(Vue·Element·`@/` 별칭 금지).
 *
 * 여덟 화면이 매장 셀렉트를 채우려고 목록 API(`GET /store`)를 부르면서, 같은 URL·같은 조건 객체·
 * 같은 후처리를 각자 한 벌씩 갖고 있었다. 그래서 그 목록이 서버 페이징으로 바뀌는 순간 여덟 벌이
 * **동시에** 틀린다 — 배열 대신 페이지 봉투가 오고, 기본 한 장은 매장 목록이 아니다.
 *
 * 그래서 축을 나눴다(2026-09-18 결정): **목록은 `GET /store`(페이지 봉투·평면 28필드)**,
 * **셀렉트는 [STORE_OPTIONS_API]**(페이징 없는 배열). 셀렉트에는 51번째 매장으로 넘어갈 페이지
 * UI 가 없으니 페이징이 뜻을 갖지 않고, 반대로 목록에는 다국어 축(`defaultLan`·`lans`)이 필요
 * 없다. 두 화면이 서로의 무게를 지지 않게 하는 것이 이 분리의 전부다.
 *
 * URL·조건·다국어 순서를 여기 한 곳에 두어 다음 회차가 같은 자리를 여덟 번 고치지 않게 한다.
 */

/**
 * 매장 셀렉트 전용 경량 조회 — 호출자 사업장 스코프, **페이징 없이 배열**로 답한다.
 *
 * 행은 `storeCd`·`storeId`·`storeNm`·`storeNmLan`·`useYn`·`internationalPayYn`·
 * `defaultLan{id,nm}`·`lans[{id,nm}]` 여덟 칸이다. 응답을 그대로 셀렉트에 꽂으므로
 * 봉투를 푸는 단계가 없다.
 *
 * **`storeCd` 를 실으면 한 행 조회**다(R-D2 S-1) — 후보에 없는 매장(범위 트리에서 고른 매장)의 셀렉트
 * 한 줄을 얻는 자리이고 `mixins/storeOptionResolver.js` 가 쓴다. 모양·범위·`useYn` 기본값이 후보 목록과
 * 같고, 범위 밖·없는 매장은 404 가 아니라 **빈 배열**이다. 종전에는 그 한 줄을 위해 매장 상세
 * (`GET /store/{storeCd}`) 전체를 받았는데 거기에는 PG 상점키가 실려 있었다.
 */
export const STORE_OPTIONS_API = '/store/combo'

/**
 * 매장 셀렉트용 조회 조건 — **사용 중인 매장 전부**.
 *
 * 매번 새 객체를 돌려준다. 호출부가 `storeCd` 같은 축을 덧붙여도 다음 호출이 영향받지 않아야 한다.
 *
 * @param {object} [over] 화면이 덧붙이는 조건
 * @returns {object} 요청에 실을 새 객체
 */
export function storeOptionsQuery(over) {
  return Object.assign({ storeNm: '', useYn: 'Y' }, over || {})
}

/**
 * 한 매장의 **다국어 입력 탭 순서** — 기본 언어가 맨 앞, 그 뒤가 추가 언어다.
 *
 * 여섯 화면이 `initStoreLans` 라는 같은 이름으로 같은 루프를 갖고 있던 것을 한 곳으로 모은다.
 * 메뉴명·옵션명의 언어별 칸이 이 순서를 그대로 쓰므로(첫 칸 = 기본 언어) 순서가 곧 계약이다.
 *
 * 값이 없는 축은 **건너뛴다**. 응답에서 이 둘이 빠지면 탭이 줄어들 뿐이어야지, 화면 전체가
 * 죽어서는 안 된다 — 매장 셀렉트는 메뉴·옵션·카테고리 화면의 첫 조회라 여기서 죽으면 표도 안 뜬다.
 *
 * @param {{defaultLan?: object, lans?: Array}} store
 * @returns {Array} `[기본 언어, …추가 언어]`
 */
export function storeLansOf(store) {
  const defaultLan = store && store.defaultLan
  const lans = store && Array.isArray(store.lans) ? store.lans : []

  return (defaultLan ? [defaultLan] : []).concat(lans)
}

/**
 * 응답을 셀렉트가 쓸 배열로 — **배열이 아닌 것은 빈 배열**이다.
 *
 * [STORE_OPTIONS_API] 는 배열로 답하는 계약이지만, 그 계약을 화면마다 `result.length` 로 믿으면
 * 어긋난 하루에 여덟 화면이 같이 흰 화면이 된다. 믿는 자리를 하나로 줄인다.
 *
 * @param {*} result 서버 응답
 * @returns {Array} 셀렉트 항목(언제나 배열)
 */
export function storeOptionsOf(result) {
  return Array.isArray(result) ? result : []
}

/**
 * 셀렉트의 **첫 매장** — 없으면 `null`.
 *
 * [STORE_OPTIONS_API] 는 사업장이 없는 계정(admin·mng·콜팝 관리자 등 `tbl_user` 행이 없는 계정)에
 * **정상 응답으로 빈 배열**을 준다. 화면이 `rows[0].storeCd` 를 가드 밖에서 읽으면 그 계정에서
 * 첫 조회 콜백이 TypeError 로 죽어 셀렉트도 표도 비어 버린다. 「첫 매장이 없을 수 있다」는 사실을
 * 이 함수 한 곳에 가두고, 호출부는 `null` 검사 하나만 한다.
 *
 * @param {Array} rows [storeOptionsOf] 가 돌려준 배열
 * @returns {object|null} 첫 매장 행, 없으면 `null`
 */
export function firstStoreOf(rows) {
  return Array.isArray(rows) && rows.length > 0 ? rows[0] : null
}

/**
 * 후보 목록에서 그 매장 — 없으면 `null`.
 *
 * `storeCd` 는 숫자로도 문자열로도 온다(셀렉트의 값, URL 쿼리, 폼). 비교를 화면마다 `==` 로 적으면
 * 어느 화면이 엄격 비교로 바뀌는 날 그 화면만 조용히 못 찾는다 — 비교 규칙을 한 곳에 둔다.
 *
 * @param {Array} options [storeOptionsOf] 가 돌려준 배열
 * @param {number|string} storeCd
 * @returns {object|null} 후보 행, 없으면 `null`
 */
export function findStoreOption(options, storeCd) {
  if (storeCd == null || storeCd === '') return null
  const key = String(storeCd)
  const rows = Array.isArray(options) ? options : []

  return rows.find((row) => row && String(row.storeCd) === key) || null
}
